/**
 * The single chrome surface for every protected route.
 *
 * Story 050 made it sidebar-only (no top AppBar; the page's own H1 is the
 * topmost element of `<main>`). WS-NAV keeps that on the desktop and adds the
 * responsive branch: below MUI's `md` breakpoint the sidebar is replaced by
 * `MobileNav` (top bar + drawer) and `<main>` is inset below the fixed bar.
 * There is still exactly ONE primary-navigation landmark in the document at
 * any viewport.
 *
 * SB6-AUTH-RECONNECT-01 — the proactive-refresh hook + activity tracker +
 * soft-prompt modal mount here. AppChrome only renders for authenticated
 * users (the route tree wraps it in <RequireAuth/>), so the timer cleanly
 * tears down on sign-out and never fires on /login.
 */
import type { PropsWithChildren } from "react";
import { useEffect } from "react";
import { Box } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { DENSITY_ATTR } from "@aeriosiq/ui";
import {
  SessionExpiredModal,
  installAuthBroadcast,
  useActivityTracker,
  useHasRole,
  useProactiveTokenRefresh,
} from "@aeriosiq/auth-client";
import { AppSidebar } from "./AppSidebar.js";
import { MOBILE_BAR_HEIGHT, MobileNav } from "./MobileNav.js";
import { useIsMobileViewport } from "../workspaces/useWorkspaces.js";
import { useWorkspaceShortcuts } from "./workspaceShortcuts.js";
import { IntakeResumeHost } from "./IntakeResumeHost.js";
import { PillDock } from "./PillDock.js";
// DL-02 — image downloads outlive their dialog: the manager lives here, above
// every protected route, and its status bar + "Downloads" modal mount once.
import { DownloadManagerHost, DownloadManagerProvider } from "@aeriosiq/lyse";

export function AppChrome({ children }: PropsWithChildren) {
  // DL-04 — a platform admin's downloads carry `bundle.json` 2.0 beside each
  // data set's `manifest.json` (the export-bundle read is platform-admin
  // only server-side; this only decides whether the engine asks for it).
  const isPlatformAdmin = useHasRole("platform_admin");
  return (
    <DownloadManagerProvider writeExportBundle={isPlatformAdmin}>
      <AppChromeInner>{children}</AppChromeInner>
    </DownloadManagerProvider>
  );
}

function AppChromeInner({ children }: PropsWithChildren) {
  // SB6-AUTH-RECONNECT-01 — install the three session-lifetime side effects
  // exactly once at the shell. Order is irrelevant; each is independent. The
  // proactive timer self-disables in a pop-out window (ANA-R1-09) so only the
  // main window rotates the shared refresh token.
  useProactiveTokenRefresh();
  useActivityTracker();

  // ANA-R1-09 — bridge the per-window sessionStorage so a pop-out follows the
  // main window's token rotations (and session-end) instead of refreshing on
  // its own.
  useEffect(() => installAuthBroadcast(), []);

  // WS-NAV — ⌘/Ctrl+0–6 work on every route, sidebar visible or not.
  useWorkspaceShortcuts();

  const isMobile = useIsMobileViewport();

  // SB6-ANA-POP-01 — a popped-out widget window (`?popout=1`) shows ONLY the
  // widget content: no sidebar / navigation rail, no chrome.
  const [searchParams] = useSearchParams();
  if (searchParams.get("popout") === "1") {
    return (
      <Box
        component="main"
        aria-label="Main content"
        sx={{ height: "100vh", width: "100vw", overflow: "hidden" }}
      >
        {children}
        <SessionExpiredModal />
      </Box>
    );
  }

  return (
    // 2026-05-12 — pin the outer flex to viewport height (was
    // `minHeight: 100vh`, which let long pages push the body scroll and drag
    // the sidebar off-screen with it). The main column owns its own vertical
    // scroll so the sidebar stays sticky across every route.
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      {isMobile ? <MobileNav /> : <AppSidebar />}
      <Box
        component="main"
        aria-label="Main content"
        // D77 — density is a property of a SURFACE: set once on a region and
        // inherited, never chosen per instance. This is that one place.
        //
        // The authenticated app is a dense working surface — long-session,
        // data-first, a six-level filter cascade on a 1547px strip. Compact is
        // 32px, which clears the WCAG 2.2 AA target floor with 8px to spare.
        //
        // It is deliberately NOT set on login, which lives outside this shell:
        // D77 bars compact from first-run flows and touch surfaces, and the
        // login screen is both.
        {...{ [DENSITY_ATTR]: "compact" }}
        sx={{
          flex: 1,
          minWidth: 0,
          height: "100vh",
          overflowY: "auto",
          // FB-29 — CSS promotes `overflow-x` to `auto` whenever `overflow-y`
          // is `auto`, so this <main> silently became a HORIZONTAL scroll
          // container. `clip` on x + `auto` on y clips x and scrolls y.
          overflowX: "clip",
          px: { xs: 2, md: 3 },
          // 2026-05-12 (Christian) — explicit `pt: 2.5` so the wordmark and
          // the page H1 share a horizontal baseline. On mobile the fixed top
          // bar owns that band, so the canvas starts below it.
          pt: isMobile ? `${String(MOBILE_BAR_HEIGHT + 16)}px` : 2.5,
          pb: 2,
        }}
      >
        {children}
      </Box>
      {/* SB6-AUTH-RECONNECT-01 — soft session-expired prompt. Renders nothing
          unless the auth status is 'session_expired'. */}
      <SessionExpiredModal />
      {/* DI-3-D — resume the intake from any tab (Field Data routes only). */}
      <IntakeResumeHost />
      {/* DL-02 — the image-download pill (bottom centre of the browser edge,
          hidden a minute after idle, never while paused) and the "Downloads"
          modal. */}
      <PillDock>
        <DownloadManagerHost docked />
      </PillDock>
    </Box>
  );
}
